import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { useToast } from '../context/ToastContext';

const CreateListPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [name, setName] = useState('');
  const [language1, setLanguage1] = useState('');
  const [language2, setLanguage2] = useState('');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const isSubmittingRef = useRef(false);

  const validateForm = () => {
    const errors = {};

    if (!name.trim()) {
      errors.name = t('errors.required');
    }

    if (!language1.trim()) {
      errors.language1 = t('errors.required');
    }

    if (!language2.trim()) {
      errors.language2 = t('errors.required');
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prevent concurrent submissions using ref (synchronous check)
    if (isSubmittingRef.current) {
      return;
    }
    isSubmittingRef.current = true;

    setError('');

    if (!validateForm()) {
      isSubmittingRef.current = false;
      return;
    }

    setIsLoading(true);

    try {
      const response = await api.post('/lists', {
        name: name.trim(),
        language1: language1.trim(),
        language2: language2.trim(),
      });
      showToast(t('common.success'), 'success');
      navigate(`/lists/${response.data.id}`);
    } catch (err) {
      if (err.message.includes('limit')) {
        setError(t('lists.limitReached'));
      } else {
        setError(err.message || t('errors.serverError'));
      }
    } finally {
      setIsLoading(false);
      isSubmittingRef.current = false;
    }
  };

  return (
    <div>
      <button
        className="btn btn-outline"
        onClick={() => navigate('/lists')}
        style={{ marginBottom: '1rem' }}
      >
        &larr; {t('common.back')}
      </button>

      <h1 className="page-title">{t('lists.create')}</h1>

      <form onSubmit={handleSubmit} className="card" style={{ marginTop: '1rem' }}>
        {error && (
          <div className="form-error" style={{ marginBottom: '1rem' }}>
            {error}
          </div>
        )}

        <div className="form-group">
          <label htmlFor="name" className="form-label">
            {t('lists.name')} *
          </label>
          <input
            type="text"
            id="name"
            className={`form-input ${fieldErrors.name ? 'error' : ''}`}
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
          {fieldErrors.name && (
            <div className="form-error">{fieldErrors.name}</div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="language1" className="form-label">
            {t('lists.language1')} *
          </label>
          <input
            type="text"
            id="language1"
            className={`form-input ${fieldErrors.language1 ? 'error' : ''}`}
            value={language1}
            onChange={(e) => setLanguage1(e.target.value)}
            placeholder={t('lists.language1Placeholder')}
          />
          {fieldErrors.language1 && (
            <div className="form-error">{fieldErrors.language1}</div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="language2" className="form-label">
            {t('lists.language2')} *
          </label>
          <input
            type="text"
            id="language2"
            className={`form-input ${fieldErrors.language2 ? 'error' : ''}`}
            value={language2}
            onChange={(e) => setLanguage2(e.target.value)}
            placeholder={t('lists.language2Placeholder')}
          />
          {fieldErrors.language2 && (
            <div className="form-error">{fieldErrors.language2}</div>
          )}
        </div>

        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isLoading}
          >
            {isLoading ? t('common.loading') : t('common.create')}
          </button>
          <button
            type="button"
            className="btn btn-outline"
            onClick={() => navigate('/lists')}
          >
            {t('common.cancel')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateListPage;
